/**
 * Tenant Metadata Type Definitions
 *
 * These types mirror the backend domain models from:
 * - backend/src/main/java/com/goflux/exposeapi/tenant/domain/TenantMetadata.java
 * - backend/src/main/java/com/goflux/exposeapi/tenant/domain/TenantType.java
 * - backend/src/main/java/com/goflux/exposeapi/tenant/domain/HostingTier.java
 *
 * Session 2B: Frontend Routing by Tenant Type
 */

// ============================================================================
// Enums
// ============================================================================

/**
 * TenantType - Categorizes tenants based on X-Road membership and hosting model
 *
 * Maps to backend TenantType enum
 */
export enum TenantType {
  /**
   * SENUM (Central Authority)
   * - Operates the X-Road instance
   * - Manages all members
   * - Can view all tenants and services
   */
  CENTRAL_AUTHORITY = 'CENTRAL_AUTHORITY',

  /**
   * Self-Managed Member
   * - Operates own Security Server
   * - Can publish unlimited services
   * - Can create subsystems
   */
  MEMBER_SELF_MANAGED = 'MEMBER_SELF_MANAGED',

  /**
   * Hosted Member (Standard/Medium/Large)
   * - Security Server hosted by Control Tower
   * - Service limits based on hosting tier
   * - Can create subsystems
   */
  MEMBER_HOSTED = 'MEMBER_HOSTED',

  /**
   * Hosted Member (Light Tier)
   * - Security Server hosted by Control Tower
   * - Limited services (5-10)
   * - Cannot create subsystems
   */
  MEMBER_HOSTED_LIGHT = 'MEMBER_HOSTED_LIGHT',

  /**
   * Subsystem (Hosted)
   * - Child of hosted member
   * - Shares parent's service limits
   * - Cannot create own subsystems
   */
  SUBSYSTEM_HOSTED = 'SUBSYSTEM_HOSTED',

  /**
   * Subsystem (Self-Managed)
   * - Child of self-managed member
   * - No service limits
   * - Cannot create own subsystems
   */
  SUBSYSTEM_INTERNAL = 'SUBSYSTEM_INTERNAL',
}

/**
 * HostingTier - Resource allocation tier for hosted members
 *
 * Maps to backend HostingTier enum
 */
export enum HostingTier {
  /**
   * Light Tier
   * - Max 5-10 services
   * - 100K API calls/month
   * - 1 subsystem
   */
  LIGHT = 'LIGHT',

  /**
   * Standard Tier
   * - Max 25 services
   * - 500K API calls/month
   * - 3 subsystems
   */
  STANDARD = 'STANDARD',

  /**
   * Medium Tier
   * - Max 50 services
   * - 2M API calls/month
   * - 10 subsystems
   */
  MEDIUM = 'MEDIUM',

  /**
   * Large Tier
   * - Max 100 services
   * - 10M API calls/month
   * - 20 subsystems
   */
  LARGE = 'LARGE',
}

// ============================================================================
// Domain Types
// ============================================================================

/**
 * TenantMetadata - Complete tenant metadata
 *
 * Maps to backend TenantMetadata interface
 */
export interface TenantMetadata {
  // Identity
  tenantId: string;
  tenantType: TenantType;
  parentTenantId?: string;

  // X-Road Identification
  xroadInstance: string;
  xroadMemberClass: string;
  xroadMemberCode: string;
  xroadSubsystemCode?: string;
  xroadMemberIdentifier: string; // Full identifier: "INSTANCE/CLASS/CODE/SUBSYSTEM"

  // Hosting Configuration (for MEMBER_HOSTED and MEMBER_HOSTED_LIGHT)
  hostingTier?: HostingTier;

  // Resource Limits
  maxServices?: number;
  maxApiCallsPerMonth?: number;
  maxSubsystems?: number;

  // Status
  isActive: boolean;

  // Audit
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;

  // Metadata (JSON)
  metadata?: Record<string, any>;
}

/**
 * TenantServiceUsage - Service usage statistics for a tenant
 *
 * Maps to backend TenantMetadataRepository.TenantServiceUsage record
 */
export interface TenantServiceUsage {
  tenantId: string;
  tenantType: TenantType;
  maxServices?: number;
  currentServices: number;
  remainingServices?: number;
  usagePercentage: number;
}

// ============================================================================
// Request/Response DTOs
// ============================================================================

/**
 * CreateTenantRequest - Request to create a new tenant
 */
export interface CreateTenantRequest {
  tenantId: string;
  tenantType: TenantType;
  parentTenantId?: string;

  // X-Road Identification
  xroadInstance: string;
  xroadMemberClass: string;
  xroadMemberCode: string;
  xroadSubsystemCode?: string;

  // Hosting Configuration
  hostingTier?: HostingTier;
  maxServices?: number;
  maxApiCallsPerMonth?: number;
  maxSubsystems?: number;
}

/**
 * UpdateTenantRequest - Request to update tenant metadata
 */
export interface UpdateTenantRequest {
  hostingTier?: HostingTier;
  maxServices?: number;
  maxApiCallsPerMonth?: number;
  maxSubsystems?: number;
  isActive?: boolean;
}

/**
 * SearchTenantsQuery - Query parameters for searching tenants
 */
export interface SearchTenantsQuery {
  tenantType?: TenantType;
  parentTenantId?: string;
  hostingTier?: HostingTier;
  offset?: number;
  limit?: number;
}

// ============================================================================
// UI-Specific Types
// ============================================================================

/**
 * TenantPortalType - Portal UI type based on tenant type
 * Used for routing and conditional rendering
 */
export type TenantPortalType =
  | 'operator-console'      // CENTRAL_AUTHORITY (SENUM)
  | 'control-tower'         // MEMBER_SELF_MANAGED
  | 'managed-portal'        // MEMBER_HOSTED
  | 'light-portal';         // MEMBER_HOSTED_LIGHT

/**
 * TenantTypeMetadata - UI metadata for each tenant type
 */
export interface TenantTypeMetadata {
  type: TenantType;
  label: string;
  description: string;
  portalType: TenantPortalType;
  color: string;       // Tailwind color class
  icon: string;        // Icon identifier
  canPublishServices: boolean;
  canCreateSubsystems: boolean;
  canViewAllTenants: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get portal type from tenant type
 */
export function getPortalType(tenantType: TenantType): TenantPortalType {
  switch (tenantType) {
    case TenantType.CENTRAL_AUTHORITY:
      return 'operator-console';
    case TenantType.MEMBER_SELF_MANAGED:
      return 'control-tower';
    case TenantType.MEMBER_HOSTED:
      return 'managed-portal';
    case TenantType.MEMBER_HOSTED_LIGHT:
      return 'light-portal';
    case TenantType.SUBSYSTEM_HOSTED:
    case TenantType.SUBSYSTEM_INTERNAL:
      // Subsystems use same portal as parent
      return 'managed-portal';
  }
}

/**
 * Get tenant type metadata for UI rendering
 */
export function getTenantTypeMetadata(tenantType: TenantType): TenantTypeMetadata {
  const metadata: Record<TenantType, TenantTypeMetadata> = {
    [TenantType.CENTRAL_AUTHORITY]: {
      type: TenantType.CENTRAL_AUTHORITY,
      label: 'Central Authority (SENUM)',
      description: 'Operates the X-Road instance and manages all members',
      portalType: 'operator-console',
      color: 'purple',
      icon: 'shield-check',
      canPublishServices: true,
      canCreateSubsystems: false,
      canViewAllTenants: true,
    },
    [TenantType.MEMBER_SELF_MANAGED]: {
      type: TenantType.MEMBER_SELF_MANAGED,
      label: 'Self-Managed Member',
      description: 'Operates own Security Server with unlimited services',
      portalType: 'control-tower',
      color: 'blue',
      icon: 'server',
      canPublishServices: true,
      canCreateSubsystems: true,
      canViewAllTenants: false,
    },
    [TenantType.MEMBER_HOSTED]: {
      type: TenantType.MEMBER_HOSTED,
      label: 'Hosted Member',
      description: 'Security Server hosted by Control Tower',
      portalType: 'managed-portal',
      color: 'green',
      icon: 'cloud',
      canPublishServices: true,
      canCreateSubsystems: true,
      canViewAllTenants: false,
    },
    [TenantType.MEMBER_HOSTED_LIGHT]: {
      type: TenantType.MEMBER_HOSTED_LIGHT,
      label: 'Hosted Member (Light)',
      description: 'Light tier with limited services',
      portalType: 'light-portal',
      color: 'yellow',
      icon: 'cloud-arrow-up',
      canPublishServices: true,
      canCreateSubsystems: false,
      canViewAllTenants: false,
    },
    [TenantType.SUBSYSTEM_HOSTED]: {
      type: TenantType.SUBSYSTEM_HOSTED,
      label: 'Subsystem (Hosted)',
      description: 'Child of hosted member',
      portalType: 'managed-portal',
      color: 'indigo',
      icon: 'cube',
      canPublishServices: false,
      canCreateSubsystems: false,
      canViewAllTenants: false,
    },
    [TenantType.SUBSYSTEM_INTERNAL]: {
      type: TenantType.SUBSYSTEM_INTERNAL,
      label: 'Subsystem (Self-Managed)',
      description: 'Child of self-managed member',
      portalType: 'managed-portal',
      color: 'cyan',
      icon: 'cube-transparent',
      canPublishServices: false,
      canCreateSubsystems: false,
      canViewAllTenants: false,
    },
  };

  return metadata[tenantType];
}

/**
 * Get hosting tier metadata for UI rendering
 */
export function getHostingTierMetadata(tier: HostingTier) {
  const metadata = {
    [HostingTier.LIGHT]: {
      label: 'Light',
      maxServices: 10,
      maxApiCalls: '100K',
      maxSubsystems: 1,
      color: 'yellow',
    },
    [HostingTier.STANDARD]: {
      label: 'Standard',
      maxServices: 25,
      maxApiCalls: '500K',
      maxSubsystems: 3,
      color: 'blue',
    },
    [HostingTier.MEDIUM]: {
      label: 'Medium',
      maxServices: 50,
      maxApiCalls: '2M',
      maxSubsystems: 10,
      color: 'green',
    },
    [HostingTier.LARGE]: {
      label: 'Large',
      maxServices: 100,
      maxApiCalls: '10M',
      maxSubsystems: 20,
      color: 'purple',
    },
  };

  return metadata[tier];
}

/**
 * Check if tenant can perform specific actions
 */
export function canPublishServices(tenantType: TenantType): boolean {
  return getTenantTypeMetadata(tenantType).canPublishServices;
}

export function canCreateSubsystems(tenant: TenantMetadata): boolean {
  const metadata = getTenantTypeMetadata(tenant.tenantType);
  if (!metadata.canCreateSubsystems) return false;

  // Check subsystem limit
  if (tenant.maxSubsystems !== undefined && tenant.maxSubsystems <= 0) {
    return false;
  }

  return true;
}

export function canViewAllTenants(tenantType: TenantType): boolean {
  return getTenantTypeMetadata(tenantType).canViewAllTenants;
}

export function canPublishService(tenant: TenantMetadata, currentServiceCount: number): boolean {
  if (!canPublishServices(tenant.tenantType)) return false;

  // Unlimited for SENUM and Self-Managed
  if (tenant.tenantType === TenantType.CENTRAL_AUTHORITY ||
      tenant.tenantType === TenantType.MEMBER_SELF_MANAGED) {
    return true;
  }

  // Check service limit for hosted members
  if (tenant.maxServices === undefined) return true;
  return currentServiceCount < tenant.maxServices;
}
